// Explications en français des situations rencontrées pendant la configuration du piano :
// autorisation refusée, absence de son, reconnaissance incertaine, piano MIDI absent… et
// choix des contenus compatibles avec la source retenue. Fonctions pures, testées sans DOM.

import type { InputKind } from '../engine/types';
import type { InputStatus } from '../input/types';
import { CATALOG } from '../music/catalog';
import { hasChords, type Hand, type Score } from '../music/model';
import type { Capabilities } from './platform';

export interface Explanation {
  title: string;
  detail: string;
  steps: string[];
}

/** Version courte pour la carte du choix de source. */
export const MIC_LIMIT_SHORT = 'Une note à la fois : jouez une main à la fois, ou passez au MIDI pour les accords.';

/** Limite actuelle du micro, rappelée au choix de la source et au lancement d'un exercice. */
export const MIC_LIMIT_TEXT =
  'Le micro reconnaît les notes jouées une à la fois. Deux notes jouées en même temps (un accord, les deux mains ensemble) ne sont pas séparées dans cette version : jouez une main à la fois, ou utilisez un piano connecté en MIDI.';

function micPermissionSteps(caps: Capabilities): string[] {
  switch (caps.os) {
    case 'ios':
      return [
        'Dans Safari, touchez « AA » (ou l’icône de réglages) à gauche de la barre d’adresse, puis « Réglages du site web » et mettez « Micro » sur « Autoriser ».',
        'Sinon : Réglages de l’iPhone ou de l’iPad → Safari → Micro → « Demander » ou « Autoriser ».',
        'Si vous utilisez un autre navigateur (Chrome, Firefox…) : Réglages → Confidentialité et sécurité → Micro → activez ce navigateur.',
        'Revenez sur cette page et touchez à nouveau « Activer le micro ».',
      ];
    case 'android':
      return [
        'Touchez le cadenas (ou l’icône de réglages) à gauche de l’adresse → Autorisations → Micro → Autoriser.',
        'Si le micro n’est pas proposé : Paramètres du téléphone → Applications → votre navigateur → Autorisations → Micro.',
        'Revenez sur cette page et touchez à nouveau « Activer le micro ».',
      ];
    default:
      return [
        'Cliquez sur le cadenas (ou l’icône de micro barré) dans la barre d’adresse, puis autorisez le micro pour ce site.',
        'Rechargez la page si le navigateur le demande, puis cliquez à nouveau sur « Activer le micro ».',
        'Sur Windows et macOS, vérifiez aussi que le système autorise ce navigateur à utiliser le micro (Confidentialité → Microphone).',
      ];
  }
}

/** Explication d'un état d'entrée problématique, ou null si tout va bien. */
export function explainStatus(status: InputStatus, caps: Capabilities): Explanation | null {
  const code = status.code;
  if (!code) return null;
  const midi = status.kind === 'midi';
  switch (code) {
    case 'denied':
      return midi
        ? {
            title: 'Accès MIDI refusé',
            detail: 'Le navigateur a bloqué l’accès aux périphériques MIDI pour ce site.',
            steps: ['Cliquez sur le cadenas dans la barre d’adresse et autorisez les périphériques MIDI.', 'Rechargez la page, puis cliquez à nouveau sur « Connecter le piano MIDI ».'],
          }
        : {
            title: 'Autorisation du micro refusée',
            detail: 'Le navigateur a bloqué l’accès au micro pour ce site. Sans cette autorisation, l’application ne peut pas entendre votre piano. Rien n’est enregistré ni envoyé : le son est analysé sur votre appareil.',
            steps: micPermissionSteps(caps),
          };
    case 'insecure':
      return { title: 'Page ouverte sans HTTPS', detail: status.message, steps: ['Ouvrez l’adresse qui commence par https:// (ou http://localhost sur l’ordinateur de développement).'] };
    case 'unsupported':
      return {
        title: midi ? 'MIDI non disponible dans ce navigateur' : 'Micro non disponible dans ce navigateur',
        detail: midi ? (caps.midi.reason ?? status.message) : (caps.mic.reason ?? status.message),
        steps: midi ? ['Choisissez « Micro intégré », ou ouvrez pianoHome dans Chrome, Edge ou Firefox sur un ordinateur.'] : ['Essayez une version récente de Safari, Chrome, Edge ou Firefox.'],
      };
    case 'notFound':
      return {
        title: 'Aucun micro trouvé',
        detail: 'Le navigateur ne voit aucun micro sur cet appareil.',
        steps: ['Sur un ordinateur : branchez un micro ou un casque avec micro, ou vérifiez qu’il n’est pas désactivé dans les réglages du système.', 'Sur une tablette ou un téléphone : vérifiez qu’aucun accessoire ne bloque le micro, puis réessayez.'],
      };
    case 'busy':
      return {
        title: 'Micro occupé par une autre application',
        detail: 'Une autre application (visioconférence, dictaphone, enregistrement) utilise déjà le micro.',
        steps: ['Fermez cette application ou coupez son micro.', 'Cliquez à nouveau sur « Activer le micro ».'],
      };
    case 'noDevice':
      return {
        title: 'Aucun piano MIDI détecté',
        detail: 'L’accès MIDI est accordé, mais aucun piano n’apparaît. La liste se met à jour dès que le piano est branché et allumé.',
        steps: [
          'Allumez le piano, puis branchez le câble USB sur la prise « USB to Host » du piano et sur l’ordinateur (adaptateur USB‑C si besoin).',
          'Débranchez et rebranchez le câble si le piano était déjà branché avant l’ouverture de la page.',
          'Sur Windows, fermez les autres logiciels de musique qui pourraient monopoliser le piano.',
          'Bluetooth MIDI : associez d’abord le piano dans les réglages Bluetooth du système.',
        ],
      };
    case 'interrupted':
      return {
        title: 'Micro suspendu par le système',
        detail: 'L’appareil a suspendu le micro : page passée en arrière-plan, écran verrouillé, appel ou autre application audio.',
        steps: ['Revenez sur cette page et gardez-la au premier plan.', 'Si le son ne revient pas, touchez à nouveau « Activer le micro ».'],
      };
    case 'lost':
      return {
        title: midi ? 'Piano MIDI débranché' : 'Micro déconnecté',
        detail: status.message,
        steps: [midi ? 'Rebranchez le câble : la connexion reprend automatiquement.' : 'Rebranchez le micro ou le casque, puis touchez « Activer le micro ».'],
      };
    default:
      return { title: midi ? 'Problème MIDI' : 'Problème de micro', detail: status.message, steps: ['Réessayez. Si le problème persiste, consultez la page Aide.'] };
  }
}

export type CheckTone = 'info' | 'ok' | 'warn';

export interface SoundCheckInput {
  kind: InputKind;
  /** La source choisie est bien active. */
  active: boolean;
  /** Du son a dépassé nettement le bruit de fond depuis le début de l'étape. */
  heard: boolean;
  /** Bruit de fond élevé. */
  noisy: boolean;
  /** Détections sûres et incertaines depuis le début de l'étape. */
  sure: number;
  uncertain: number;
  /** Temps écoulé sans détection (ms). */
  idleMs: number;
}

/** Délai d'inactivité au-delà duquel on explique l'absence de son ou de note. */
export const IDLE_HINT_MS = 7000;

/** Une phrase d'état pour l'étape d'écoute et l'essai : ce qui est reçu, ou pourquoi rien ne vient. */
export function describeSoundCheck(input: SoundCheckInput): { tone: CheckTone; text: string } {
  const { kind, active, heard, noisy, sure, uncertain, idleMs } = input;
  if (!active) {
    return { tone: 'warn', text: kind === 'midi' ? 'Le piano MIDI n’est pas connecté.' : kind === 'keyboard' ? 'Le clavier d’essai n’est pas actif.' : 'Le micro n’est pas actif.' };
  }
  if (kind === 'midi') {
    if (sure > 0) return { tone: 'ok', text: 'Le piano envoie ses notes : la connexion MIDI fonctionne.' };
    if (idleMs > IDLE_HINT_MS) return { tone: 'warn', text: 'Aucune note reçue. Vérifiez que le piano est allumé, que le câble est branché des deux côtés et que le bon périphérique est choisi.' };
    return { tone: 'info', text: 'Appuyez sur une touche du piano.' };
  }
  if (kind === 'keyboard') {
    return sure > 0 ? { tone: 'ok', text: 'Le clavier d’essai fonctionne.' } : { tone: 'info', text: 'Appuyez sur une touche.' };
  }
  if (sure > 0 && uncertain > sure) {
    return { tone: 'warn', text: 'Des notes sont reconnues, mais souvent avec doute : jouez une seule note à la fois, plus fort ou plus près de l’appareil, sans pédale.' };
  }
  if (sure > 0) {
    return noisy
      ? { tone: 'warn', text: 'Notes reconnues, mais le bruit de fond est élevé : fermez les fenêtres, coupez la télévision ou rapprochez l’appareil du piano.' }
      : { tone: 'ok', text: 'Le micro reçoit du son et reconnaît des notes.' };
  }
  if (uncertain > 0) {
    return { tone: 'warn', text: 'Du son arrive, mais la note n’est pas reconnue avec certitude : jouez une seule note, franche, tenue une seconde, sans pédale.' };
  }
  if (heard && idleMs > IDLE_HINT_MS) {
    return { tone: 'warn', text: 'Du son arrive, mais aucune note n’est reconnue. Jouez une seule note, tenue une seconde, sans pédale ; évitez de parler pendant l’essai.' };
  }
  if (!heard && idleMs > IDLE_HINT_MS) {
    return { tone: 'warn', text: 'Aucun son ne parvient au micro. Vérifiez qu’il n’est pas coupé, rapprochez l’appareil du piano (moins d’un mètre) et jouez une note franche.' };
  }
  return heard ? { tone: 'info', text: 'Son reçu. Jouez une note et laissez-la sonner.' } : { tone: 'info', text: 'Jouez une note : le niveau du micro et la note reconnue s’affichent ici.' };
}

export type Compatibility = 'ok' | 'oneHand' | 'midiOnly';

/** Deux mains qui jouent au même instant : le micro ne les sépare pas. */
export function handsOverlap(score: Score): boolean {
  if (score.parts.length < 2) return false;
  const starts = new Set(score.parts[0].notes.map((n) => n.start));
  return score.parts.slice(1).some((part) => part.notes.some((n) => starts.has(n.start)));
}

/** Contenus du catalogue et leur compatibilité avec la source choisie. */
export function compatibleScores(kind: InputKind, catalog: readonly Score[] = CATALOG): { score: Score; mode: Compatibility }[] {
  return catalog.map((score) => {
    if (kind === 'midi' || kind === 'keyboard') return { score, mode: 'ok' as const };
    if (hasChords(score)) return { score, mode: 'midiOnly' as const };
    return { score, mode: handsOverlap(score) ? ('oneHand' as const) : ('ok' as const) };
  });
}

/** Le micro (et la synthèse, qui suit les mêmes règles) n'entend qu'une note à la fois. */
export function isMonophonic(kind: InputKind | null | undefined): boolean {
  return kind === 'mic' || kind === 'synth';
}

/**
 * Mains ouvertes par défaut pour un contenu : au micro, une seule main quand les deux jouent
 * en même temps (la main droite, qui porte la mélodie). Règle unique pour l'assistant,
 * l'accueil et le catalogue, appliquée à l'ouverture de l'écran de pratique.
 */
export function defaultHands(score: Score, kind: InputKind | null | undefined): Hand[] {
  const all = score.parts.map((p) => p.hand);
  if (isMonophonic(kind) && handsOverlap(score)) return [all.includes('right') ? 'right' : all[0]];
  return all;
}

/** Avertissement à afficher quand la source ne peut pas suivre les mains choisies, ou null. */
export function handsWarning(score: Score, hands: readonly Hand[], kind: InputKind | null | undefined): string | null {
  if (kind !== null && kind !== undefined && !isMonophonic(kind)) return null;
  if (hasChords(score, hands)) return 'Cet exercice contient des accords : le micro ne sépare pas les notes simultanées, préférez le MIDI.';
  if (hands.length > 1 && handsOverlap(score)) return 'Au micro, jouez une main à la fois : les deux mains jouent ici en même temps. Choisissez MD ou MG.';
  return null;
}
